import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import Sticky from 'react-sticky-el';
import { motion } from 'framer-motion';
import { rgba } from 'polished';
import styled, { css, useTheme } from 'styled-components';

import useMediaQuery from '@/utils/useMediaQuery';

import { CloseIcon } from '../../../icons/Icons';
import { ReactComponent as IconMenu } from '../../../images/icons/icon-menu.svg';
import { STICKY } from '../../../utils';
import { Button } from '../../design-system';
import { Ticker } from '../Ticker';

import { NavContentsDesktop } from './NavContentsDesktop';
import { NavContentsMobile } from './NavContentsMobile';
import { NavLogo } from './NavLogo';

const HeaderContainer = styled(motion.div)<{
  $collapse: boolean;
  $noMargin: boolean;
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  z-index: 999999;
  margin-bottom: ${({ $noMargin }) => ($noMargin ? '0' : '60px')};

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    margin-bottom: 20px;
  }
`;

const Header = styled.div<{ $centered: boolean; $isMobileMenuOpen: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  padding: 20px 40px;
  position: relative;
  margin: 0 auto;
  gap: 30px;

  ${({ $centered }) =>
    $centered &&
    css`
      flex-direction: column;
      justify-content: center;
    `}

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    padding: 20px;

    ${({ $isMobileMenuOpen }) =>
      $isMobileMenuOpen &&
      css`
        && {
          overflow: auto;
          flex-direction: column;
        }
      `};
  }
`;

const StyledSticky = styled(Sticky)<{ $isMobileMenuOpen: boolean }>`
  display: flex;
  width: 100%;
  margin-top: 10px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    margin-top: 0px;
  }

  /* Not yet sticky */
  > div {
    display: flex;
    width: 100%;
  }

  /* Sticky */
  .isSticky {
    background: ${({ theme }) => theme.nav.sticky.backgroundColor};
    -webkit-backdrop-filter: blur(8px);
    backdrop-filter: blur(8px);
    box-shadow: 0 1px 0 0 ${({ theme }) => theme.borderColor.DEFAULT};
    border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
    border-top: 0;
    border-bottom: 0;

    @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
      border: none;
    }
  }

  ${({ $isMobileMenuOpen }) =>
    $isMobileMenuOpen &&
    css`
      &&& {
        /* Target both non sticky and sticky states */
        > div,
        .isSticky {
          position: fixed;
          display: block;
          z-index: 1;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          height: 100vh;
          height: 100dvh;
          width: 100vw !important;
          background: ${({ theme }) => rgba(theme.backgroundColor, 0.9)};
          backdrop-filter: blur(8px);
          box-shadow: 0 1px 0 0 ${({ theme }) => theme.borderColor.DEFAULT};
          overflow-y: scroll;
          -webkit-backdrop-filter: blur(8px);
          backdrop-filter: blur(8px);
        }

        .isSticky {
          padding-top: 0;
        }
      }
    `}
`;

const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  flex: none;
  width: 100%;
  padding-left: 10px;
`;

const MobileMenuButton = styled(Button)`
  margin-left: auto;
`;

const MobileOnly = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: contents;
  }
`;

const DesktopOnly = styled.div`
  display: contents;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: none;
  }
`;

export interface NavProps {
  centered?: boolean;
  hideTicker?: boolean;
  isSticky?: boolean;
  noMargin?: boolean;
}

export const Nav: React.ComponentType<NavProps> = React.memo(
  ({
    noMargin = false,
    centered = true,
    isSticky = true,
    hideTicker = false
  }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.mediaQueryString.LARGE);

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

    const _toggleMobileMenu = useCallback(() => {
      setIsMobileMenuOpen(!isMobileMenuOpen);
    }, [isMobileMenuOpen]);

    // Close mobile menu if browser was resized
    useEffect(() => {
      if (isMobileMenuOpen && !isMobile) {
        _toggleMobileMenu();
      }
    }, [isMobile, isMobileMenuOpen, _toggleMobileMenu]);

    const header = (
      <Header $centered={centered} $isMobileMenuOpen={isMobileMenuOpen}>
        <DesktopOnly>
          {!centered && <NavLogo />}
          <NavContentsDesktop centered={centered} />
        </DesktopOnly>
        <MobileOnly>
          <MobileHeader>
            <NavLogo isMobile={true} />
            <MobileMenuButton
              icon={isMobileMenuOpen ? <CloseIcon /> : <IconMenu />}
              size="small"
              stylez="minimal"
              onClick={_toggleMobileMenu}
            />
          </MobileHeader>
          {isMobileMenuOpen && <NavContentsMobile />}
        </MobileOnly>
      </Header>
    );

    return (
      <HeaderContainer
        $collapse={isMobile}
        $noMargin={noMargin}
        initial={{ opacity: 0 }}
        transition={{ delay: 0 }}
        viewport={{ once: true }}
        whileInView={{ opacity: 1 }}>
        {!hideTicker && <Ticker />}
        {isSticky ? (
          <StyledSticky
            $isMobileMenuOpen={isMobileMenuOpen}
            dontUpdateHolderHeightWhenSticky={isMobileMenuOpen}
            stickyClassName={STICKY.Element}>
            {header}
          </StyledSticky>
        ) : (
          header
        )}
      </HeaderContainer>
    );
  }
);
